/**
 * PostHog Frontend Product Analytics
 *
 * Initializes posthog-js for client-side analytics, pageviews, and session
 * replay in the React application. This is separate from the Rails backend
 * PostHog configuration (config/initializers/posthog.rb).
 *
 * Design mirrors utils/sentry.ts:
 * - Config comes from build-time Vite env vars (VITE_PUBLIC_POSTHOG_*), so each
 *   deployed environment points at its own PostHog project token.
 * - Only enabled in production / development; dark in test and local otherwise.
 *
 * Privacy:
 * - Capturing is opt-out by default and only turns on once the visitor grants
 *   the `analytics` consent category (consent/core.ts). Revoking consent stops
 *   capture and session replay immediately.
 * - Autocapture is OFF (explicit events only). Session replay masks all inputs
 *   and any element tagged [data-ph-mask].
 *
 * Campaign attribution:
 * - Landing-URL UTMs are stashed in sessionStorage (utils/utm.ts) before
 *   consent. Once analytics is granted they are registered as session super
 *   properties, so every event in the tab carries them, and `identifyPostHog`
 *   writes them once to the person as `$initial_utm_*`.
 */

import posthog from "posthog-js";
import { getState, subscribe, type ConsentState } from "../consent/core";
import type { User } from "../contexts/AuthContext";
import { readStashedUtm, stashUtmParams, toInitialUtmProperties } from "./utm";

/**
 * Environments where PostHog should be enabled (matches the Sentry policy).
 */
const ENABLED_ENVIRONMENTS = ["production", "development"];

/**
 * Get the PostHog project token from build-time Vite env vars.
 */
function getPostHogToken(): string | undefined {
  return import.meta.env.VITE_PUBLIC_POSTHOG_TOKEN;
}

/**
 * Get the PostHog ingestion host. Defaults to our managed reverse proxy, which
 * forwards to PostHog US cloud and keeps capture working behind ad blockers.
 */
function getPostHogHost(): string {
  return (
    import.meta.env.VITE_PUBLIC_POSTHOG_HOST || "https://p.studio.activesnap.ai"
  );
}

/**
 * Get the current environment from build-time Vite env vars.
 */
function getPostHogEnvironment(): string {
  return import.meta.env.VITE_PUBLIC_POSTHOG_ENVIRONMENT || "development";
}

/**
 * Whether PostHog is enabled: a token is present and the environment is valid.
 */
export function isPostHogEnabled(): boolean {
  return (
    Boolean(getPostHogToken()) &&
    ENABLED_ENVIRONMENTS.includes(getPostHogEnvironment())
  );
}

/**
 * Apply the visitor's analytics-consent decision to PostHog.
 *
 * Opts into capturing (and session replay) only when the `analytics` category
 * is granted, then hands PostHog any stashed UTMs; opts out otherwise. Safe to
 * call repeatedly.
 *
 * @param state - the current consent state from consent/core
 */
function applyConsent(state: ConsentState): void {
  if (state.categories.analytics) {
    posthog.opt_in_capturing();
    const utm = readStashedUtm();
    if (utm) {
      posthog.register_for_session(utm);
    }
  } else {
    posthog.opt_out_capturing();
  }
}

/**
 * Initialize PostHog for the frontend.
 *
 * No-op unless {@link isPostHogEnabled}. Registers `environment` as a super
 * property on every event, stashes the landing URL's UTMs, and wires capturing
 * to the consent core.
 */
export function initPostHog(): void {
  if (!isPostHogEnabled()) {
    return;
  }

  // Stash before consent is applied so a grant on this page sees the UTMs.
  stashUtmParams(window.location.search);

  posthog.init(getPostHogToken() as string, {
    api_host: getPostHogHost(),
    // Behind the reverse proxy, links PostHog generates (toolbar, replay URLs)
    // must still point at the PostHog app, not the proxy.
    ui_host: "https://us.posthog.com",
    // Pin PostHog's config defaults snapshot; explicit options below still win.
    defaults: "2026-05-30",
    // Create person profiles for anonymous visitors too, not just logged-in users.
    person_profiles: "always",
    // Explicit events only - no automatic click/input capture.
    autocapture: false,
    // Web-vitals autocapture is remote-config-driven and ON by default, which
    // contradicts the explicit-events-only posture above. It also throws in the
    // Timeline Editor: PostHog lazy-loads Chrome's web-vitals bundle, whose
    // reportAllChanges handler reads `.startTime` off a performance entry that
    // can be undefined on a long-lived canvas-heavy page ("Cannot read
    // properties of undefined (reading 'startTime')"). network_timing only
    // feeds session replay, which is opt-in and not something we analyse.
    capture_performance: { web_vitals: false, network_timing: false },
    // Pageviews are captured manually on SPA route changes (see PostHogPageview).
    capture_pageview: false,
    capture_pageleave: true,
    // Respect the consent banner: hold all capture until analytics is granted.
    opt_out_capturing_by_default: true,
    // Session replay with PII masking: mask every input and anything tagged
    // data-ph-mask. Recording only starts once the visitor opts in.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "[data-ph-mask]",
    },
    loaded: (ph) => {
      ph.register({ environment: getPostHogEnvironment() });
    },
  });

  // Apply the stored decision now, then keep in sync with future changes.
  applyConsent(getState());
  subscribe(applyConsent);
}

/**
 * Identify the authenticated user so frontend and backend events share the
 * same distinct_id (the user UUID, matching Sentry and the Rails backend).
 *
 * When the backend ships a `posthog_identity_hash` (HMAC of the id with the
 * project secret), it is installed first via `setIdentity`, which sets the
 * `identity_distinct_id` / `identity_hash` config so Support/conversations
 * use a server-verified identity instead of an anonymous widget session.
 * `posthog.reset()` (see resetPostHog) clears it on logout.
 *
 * Stashed UTMs are written with `$set_once` as `$initial_utm_*`, so the person
 * keeps the campaign that first brought them in.
 *
 * @param user - the authenticated user
 */
export function identifyPostHog(user: User): void {
  if (!isPostHogEnabled()) {
    return;
  }

  if (user.posthog_identity_hash) {
    posthog.setIdentity(user.id, user.posthog_identity_hash);
  }

  const utm = readStashedUtm();
  posthog.identify(
    user.id,
    {
      email: user.email,
      full_name: user.full_name,
      brand_name: user.brand_name,
      provider: user.provider || "email",
      organization_id: user.organization_id,
    },
    utm ? toInitialUtmProperties(utm) : undefined,
  );
}

/**
 * Reset PostHog identity on logout so the next visitor starts anonymous.
 */
export function resetPostHog(): void {
  if (!isPostHogEnabled()) {
    return;
  }

  posthog.reset();
}

/**
 * Capture a manual SPA `$pageview` for the given path.
 *
 * @param path - the current location path (pathname + search)
 */
export function capturePostHogPageview(path: string): void {
  if (!isPostHogEnabled()) {
    return;
  }

  posthog.capture("$pageview", { $current_url: path });
}

/**
 * Capture one `$pageview` for a server-rendered page (the Devise auth pages),
 * which have no router to drive {@link capturePostHogPageview}.
 *
 * Captured right away when analytics consent is already granted; otherwise
 * held and captured once, the first time the visitor grants it on this page.
 * Call after {@link initPostHog} so PostHog is opted in before it fires.
 */
export function capturePublicPageview(): void {
  if (!isPostHogEnabled()) {
    return;
  }

  let captured = false;
  let unsubscribe: (() => void) | null = null;

  unsubscribe = subscribe((state) => {
    if (captured || !state.categories.analytics) {
      return;
    }
    captured = true;
    capturePostHogPageview(window.location.pathname + window.location.search);
    unsubscribe?.();
  });

  // subscribe() fires synchronously, before `unsubscribe` was assigned.
  if (captured) {
    unsubscribe();
  }
}
